import { GridSelectionModel } from '@mui/x-data-grid';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { FormModal } from 'components/form/FormModal/FormModal';
import { FormSelectOption } from 'components/form/types';
import { getObjectDiff } from 'helpers/general';
import { useAuth } from 'hooks';
import {
    getDeviceAlertFirebase,
    getPortalUsersStatic,
    updateDeviceAlert,
} from 'lib/firebase';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';
import {
    useLazyGetDeviceByDeviceIdEsQuery,
    useLazyGetSupplierDevicesEsQuery,
    useLazyPatchUpdateDeviceQuery,
} from 'store/services';
import { Device } from 'types';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import * as yup from 'yup';
import { BulkEditDevicesEdit } from './sections/Edit/Edit';
import { BulkEditDevicesSelection } from './sections/Selection/Selection';
import { BulkEditDevicesSummary } from './sections/Summary/Summary';
import { find, keyBy, reduce, uniq } from 'lodash';
import { AlertsConfig } from 'views/DeviceDetails/views/DeviceAlertsConfig/DeviceAlertsConfig';
import { Box, Button, Typography } from '@mui/material';
import { NoteAdd } from '@mui/icons-material';
import styles from './BulkEditDevicesModal.module.scss';

export type BulkEditOption<T> = {
    enabled: false;
    value: T;
};

export type BulkDevicesInput = {
    users: BulkEditOption<string[]>;
    addedSubscribers?: BulkEditOption<string[]>;
    removedSubscribers?: BulkEditOption<string[]>;
    tcov: BulkEditOption<number>;
    // critical threshold
    thresh1: BulkEditOption<number>;
    // warning threshold
    thresh2: BulkEditOption<number>;
    group: BulkEditOption<string>;
    ftt: BulkEditOption<number>;
    enabled: BulkEditOption<boolean>;
    thresh_enabled: BulkEditOption<boolean>;
    r_int: BulkEditOption<number>;
    t_int: BulkEditOption<number>;
};

export const defaultDeviceSettingsValues: BulkDevicesInput = {
    users: {
        enabled: false,
        value: [],
    },
    addedSubscribers: {
        enabled: false,
        value: [],
    },
    removedSubscribers: {
        enabled: false,
        value: [],
    },
    tcov: {
        enabled: false,
        value: 0,
    },
    thresh1: {
        enabled: false,
        value: 30,
    },
    thresh2: {
        enabled: false,
        value: 15,
    },
    group: {
        enabled: false,
        value: 'unchanged',
    },
    ftt: {
        enabled: false,
        value: -1,
    },
    enabled: {
        enabled: false,
        value: true,
    },
    thresh_enabled: {
        enabled: false,
        value: true,
    },
    r_int: {
        enabled: false,
        value: 6 * 60 * 60,
    },
    t_int: {
        enabled: false,
        value: 24 * 60 * 60,
    },
};

// custom yup schema object for specialized use with bulk edit
const yupBulkInputSchema = (yupSchema: yup.AnySchema) => {
    return yup.object<{ [k in keyof BulkEditOption<any>]: yup.AnySchema }>({
        enabled: yup.boolean(),
        value: yupSchema.when('enabled', {
            is: true,
            then: (schema) => schema.required(),
        }),
    });
};

const deviceSchema = yup
    .object<{ [k in keyof BulkDevicesInput]: yup.AnySchema }>({
        tcov: yupBulkInputSchema(yup.number().min(0).max(100)),
        users: yupBulkInputSchema(yup.array().of(yup.string())),
        thresh1: yupBulkInputSchema(yup.number().min(15).max(100)),
        thresh2: yupBulkInputSchema(yup.number().min(15).max(100)),
        group: yupBulkInputSchema(yup.string()),
        ftt: yupBulkInputSchema(yup.number()),
        enabled: yupBulkInputSchema(yup.boolean()),
        thresh_enabled: yupBulkInputSchema(yup.boolean()),
        r_int: yupBulkInputSchema(yup.number().min(60).max(216000)),
        t_int: yupBulkInputSchema(yup.number().min(60).max(864000)),
    })
    .required();

interface BulkEditDevicesModalProps extends DefaultOpenModalProps {
    selectionModel?: GridSelectionModel;
    query?: (...args: any[]) => Promise<any>;
    supplierId?: string;
    queryConfig?: ElasticsearchDeviceBodyConfig;
    refetchQuery?: () => void;
}

export interface SubscriberOption extends FormSelectOption {
    indeterminate?: boolean;
    hasIndeterminate?: boolean;
    checked: boolean;
}

interface AlertsConfigWithId extends AlertsConfig {
    deviceId: string;
}

export interface BulkEditDevicesModalButtonProps {
    selectionModel?: BulkEditDevicesModalProps['selectionModel'];
    supplierId?: BulkEditDevicesModalProps['supplierId'];
    queryConfig?: BulkEditDevicesModalProps['queryConfig'];
    refetchQuery?: BulkEditDevicesModalProps['refetchQuery'];
}

export function BulkEditDevicesModalButton({
    selectionModel,
    supplierId,
    queryConfig,
    refetchQuery,
}: BulkEditDevicesModalButtonProps) {
    const [modalOpen, setModalOpen] = useState(false);

    const handleClickOpen = () => {
        setModalOpen(true);
    };

    const selections = useMemo(() => {
        const selectedIds = selectionModel as string[];
        return selectedIds;
    }, [selectionModel]);

    const buttonText = useMemo(() => {
        return selections?.length === 0
            ? 'Edit All Devices'
            : selections?.length === 1
            ? `Edit ${selections.length} Device`
            : `Edit ${selections.length} Devices`;
    }, [selections]);

    return (
        <>
            <Button
                variant="text"
                onClick={handleClickOpen}
                size="small"
                startIcon={<NoteAdd />}
                className={styles.enableButton}
            >
                {buttonText}
            </Button>
            {modalOpen && (
                <BulkEditDevicesModal
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    selectionModel={selectionModel}
                    supplierId={supplierId}
                    queryConfig={queryConfig}
                    refetchQuery={refetchQuery}
                />
            )}
        </>
    );
}

export function BulkEditDevicesModal({
    modalOpen,
    setModalOpen,
    selectionModel,
    supplierId,
    queryConfig,
    refetchQuery,
}: BulkEditDevicesModalProps) {
    const selectedIds = selectionModel as string[];
    const { user } = useAuth();
    const [query] = useLazyGetSupplierDevicesEsQuery();
    const [loading, setLoading] = useState(true);
    const [devices, setDevices] = useState<Device[]>([]);
    const [deviceUserLists, setDeviceUserLists] = useState<
        Record<string, AlertsConfigWithId>
    >({});
    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);
    const dispatch = useDispatch();

    const [getDeviceByIdQuery] = useLazyGetDeviceByDeviceIdEsQuery();
    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();
    const [subscriberOptions, setSubscriberOptions] = useState<
        SubscriberOption[]
    >([]);
    const [portalUsers, setPortalUsers] = useState<any>([]);

    useEffect(() => {
        if (!modalOpen || !queryConfig) return;

        const editAll = selectedIds.length === 0;
        const editSome = selectedIds.length > 0;

        let isMounted = true; // Flag to track mount state

        if (editAll) {
            const selectionQuery = async () => {
                const querySupplierId =
                    supplierId ?? user?.protected?.supplierId ?? '';

                const initialQueryConfig: ElasticsearchDeviceBodyConfig = {
                    ...queryConfig,
                    offset: 0,
                    limit: 100,
                    isAdminUser:
                        user?.protected?.supplierId?.toString() === '0',
                };

                let total = 0;

                const { data } = await query({
                    userUid: user?.uid ?? '',
                    supplierId: querySupplierId,
                    queryConfig: initialQueryConfig,
                });

                const outDevices: Device[][] = [];

                if (data?.total) {
                    while (total * 100 < data.total && isMounted) {
                        await query({
                            userUid: user?.uid ?? '',
                            supplierId: querySupplierId,
                            queryConfig: {
                                ...initialQueryConfig,
                                offset: total * 100,
                            },
                        }).then((d) => {
                            if (d.data) {
                                outDevices.push(d.data.results);
                            }
                        });
                        total++;
                    }
                }

                return outDevices.flat(1);
            };

            selectionQuery()
                .then((d) => {
                    setLoading(false);
                    setDevices(d);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else if (editSome) {
            const promises = selectedIds.map(async (id) => {
                return await getDeviceByIdQuery({ deviceId: id, user });
            });
            Promise.all(promises)
                .then((data) => {
                    const devicesToEdit = data.map((d) => d.data?.results[0]);
                    if (devicesToEdit) {
                        setDevices(devicesToEdit as Device[]);
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        return () => {
            isMounted = false; // Set flag to false when component unmounts
        };
    }, [
        query,
        supplierId,
        user?.protected?.supplierId,
        user?.uid,
        modalOpen,
        queryConfig,
        selectedIds.length,
        selectedIds,
        user,
        getDeviceByIdQuery,
    ]);

    // Get subscriber options from the portal users for the given supplier.
    useEffect(() => {
        if (supplierId || viewAsSupplier || user?.protected?.supplierId) {
            const _supplierId =
                supplierId || viewAsSupplier || user?.protected?.supplierId;
            if (!_supplierId) return;
            getPortalUsersStatic(_supplierId).then((res) => {
                const newSubscriberOptions = res.map((r) => ({
                    value: r.email,
                    checked: false,
                }));
                setPortalUsers(res);
                setSubscriberOptions(newSubscriberOptions);
            });
        }
    }, [user?.protected?.supplierId, viewAsSupplier, supplierId]);

    // Get alert data for devices so it doesn't totally overwrite
    useEffect(() => {
        if (devices.length && (supplierId || viewAsSupplier)) {
            const promises = devices.map(async (device) => {
                return await getDeviceAlertFirebase(
                    device.deviceId,
                    viewAsSupplier ?? supplierId
                ).then((res) => ({ ...res, deviceId: device.deviceId }));
            });

            Promise.all(promises).then((result) => {
                const newDeviceUserList = keyBy(result, (a) => a.deviceId);
                setDeviceUserLists(newDeviceUserList);
                setSubscriberOptions((prevOpts) => {
                    return prevOpts.map((opt) => {
                        const hasIndeterminate = !!find(
                            newDeviceUserList,
                            (device) => device?.email?.includes(opt.value)
                        );
                        return {
                            ...opt,
                            indeterminate: hasIndeterminate,
                            hasIndeterminate,
                        };
                    });
                });
            });
        }
    }, [devices, supplierId, viewAsSupplier]);

    const mapAlertData = async (data: BulkDevicesInput) => {
        const diffedData = getObjectDiff(
            defaultDeviceSettingsValues,
            data
        ) as BulkDevicesInput;

        const alertData = Object.entries(diffedData).reduce((prev, [k, v]) => {
            return { ...prev, [k]: v.value };
        }, {} as Record<keyof BulkDevicesInput, any>);

        // delete the addedSubscribers and removedSubscribers fields from the alertData
        // These are not meant to be saved to the database, they are used for ui purposes only
        delete alertData.addedSubscribers;
        delete alertData.removedSubscribers;

        const {
            thresh1,
            thresh2,
            tcov,
            group,
            ftt,
            r_int,
            t_int,
            ...restAlertData
        } = alertData;

        const checkedUsers = subscriberOptions.filter((opt) => opt.checked); // users that need to be added
        const keptUsers = subscriberOptions.filter((opt) => opt.indeterminate); // users that remain as they are

        const formattedDeviceUserLists = data.users.enabled
            ? reduce(
                  deviceUserLists,
                  (newList, device) => {
                      const emailList = device.users
                          ? Object.values(device.users)
                          : [];
                      const filteredEmails = emailList.filter((email) =>
                          keptUsers.map((user) => user.value).includes(email)
                      );
                      const mergedEmails = uniq([
                          ...filteredEmails,
                          ...checkedUsers.map((user) => user.value),
                      ]);
                      const users = mergedEmails.reduce((userList, email) => {
                          const uid = portalUsers.find(
                              (u: any) => u.email === email
                          )?.uid;

                          return {
                              ...userList,
                              [uid]: email,
                          };
                      }, {});

                      return {
                          ...newList,
                          [device.deviceId]: {
                              ...restAlertData,
                              email: mergedEmails.join(','),
                              users,
                          },
                      };
                  },
                  {}
              )
            : {};

        // Only update the group if a selection
        const selectedGroup =
            group === 'unchanged' ? undefined : group === 'None' ? '' : group;

        const deviceData = {
            ...(thresh1 !== undefined ? { thresh1 } : {}),
            ...(thresh2 !== undefined ? { thresh2 } : {}),
            ...(tcov !== undefined ? { tcov } : {}),
            ...(ftt !== undefined ? { ftt } : {}),
            ...(r_int !== undefined ? { r_int } : {}),
            ...(t_int !== undefined ? { t_int } : {}),
            ...(selectedGroup !== undefined ? { group: selectedGroup } : {}),
        };

        const promises = devices.map(async (device) => {
            return await updateDeviceQuery({
                deviceId: device.deviceId,
                updatedFields: deviceData,
            }).then(() => {
                if (
                    viewAsSupplier ||
                    user?.protected?.supplierId?.toString() === '0'
                ) {
                    updateDeviceAlert(
                        device.deviceId,
                        viewAsSupplier,
                        formattedDeviceUserLists[
                            device.deviceId as keyof object
                        ]
                    );
                }
            });
        });

        return await Promise.all(promises);
    };

    const bulkEditFormTitle = useMemo(() => {
        return loading
            ? 'Bulk Edit'
            : devices.length === 1
            ? 'Bulk Edit (1 device)'
            : `Bulk Edit (${devices.length} devices)`;
    }, [devices, loading]);

    return (
        <FormModal
            defaultValues={defaultDeviceSettingsValues}
            validationSchema={deviceSchema}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            hideDefaultSubmit={loading}
            onValidHandler={async (alertData) => {
                return await mapAlertData(alertData).then(async () => {
                    if (refetchQuery) {
                        await refetchQuery();
                    }

                    dispatch(
                        activateSnackbar({
                            message: 'Device settings updated successfully!',
                            severity: 'success',
                        })
                    );
                });
            }}
            onClose={() => setDevices([])}
            title={bulkEditFormTitle}
        >
            {!loading ? (
                <>
                    <BulkEditDevicesSelection />
                    <BulkEditDevicesEdit
                        subscriberOptions={subscriberOptions}
                        setSubscriberOptions={setSubscriberOptions}
                        viewAsSupplier={viewAsSupplier}
                    />
                    <BulkEditDevicesSummary />
                </>
            ) : (
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                    }}
                >
                    <LoadingSpinner style={{ margin: '5px' }} />
                    <Typography variant="h6" style={{ margin: '2px' }}>
                        loading devices...
                    </Typography>
                </Box>
            )}
        </FormModal>
    );
}
